import { Component, OnInit, OnDestroy, Renderer2, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { catchError, firstValueFrom, fromEvent, map, Observable, Subscription } from 'rxjs';
import { DragulaService } from 'ng2-dragula';
import { Router, NavigationEnd } from '@angular/router';

import {
  CustomToastersService,
  FroalaCustomCommandsService,
  LayoutService,
  MaintenanceModeService,
  PowerRecallService,
  ScriptService,
  TokenStorageService
} from '@app/shared/services';
import { PowerEngageService } from '@app/shared/services/power-engage.service';
import { ChangeInitialPasswordModalComponent } from 'src/app/shared/modals/change-initial-password-modal/change-initial-password-modal.component';
import { EventsService } from '@app/shared/services/events.service';
import { SystemEvent } from './shared/enums/system-event.enum';
import { RouteHelperService } from '@app/shared/services/route-helper.service';
import { AuthService } from '@app/api/api-services/auth.service';
import { UserInfo } from './shared/models';
import { GainsightService } from './gainsight/gainsight.service';
import { EidAuthService } from './auth/services/eid-auth.service';
import { EidSessionService } from './auth/services/eid-session.service';
import { CookieService } from 'ngx-cookie-service';
import { LanguageService } from './shared/services/language.service';
import { LOGOUT_REQUESTED_MESSAGE, authBroadcastChannel } from '@shared/auth-broadcast-channel';
import { AppSettingsService } from '@api/api-services/app-settings.service';
import { HttpHeaders } from '@angular/common/http';
import { HttpClientService } from '@api/api-services/http-client.service';
import { ProfileService } from '@app/api/api-services/profile.service';
import { SubnavComponent } from './modules/common-features/subnav/subnav.component';
import { SubnavState } from '@shared/enums';
import { ConfigService } from '@app/configuration/config.service';
import { UrlSecurityService } from '@app/shared/services/url-security.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  isStandaloneRoute = false;
  private resizeSubscription: Subscription;
  public showProgramSwitcher = false;
  private dmsFeaturesInitialized = false;

  public get isAuthenticated(): boolean {
    return this.authService.isAuthenticated() && !this.routeHelper.getCurrentUrl().includes('/app/login');
  }

  public get isPlatformTopNavEnabled(): boolean {
    return this.tokenStorage.userInfo?.CurrentSite.PlatformTopNavEnabled;
  }

  public get isSidebarOpen(): boolean {
    return this.layoutService.isSidebarOpen;
  }

  public set isSidebarOpen(val) {
    this.layoutService.isSidebarOpen = val;
  }

  public get isMaintenanceMode() {
    return this.maintenanceModeService.isMaintenanceMode;
  }

  public isDropupOpened: boolean;
  public isNightMode = false;

  @ViewChild(SubnavComponent) subnavComponent: SubnavComponent;

  private subscriptions = new Array<Subscription>();
  private timers: number[] = [];

  public get isSubNavCollapsed(): boolean {
    return this.layoutService.subnavState === 'collapsed';
  }

  public get isSubNavFull(): boolean {
    return this.layoutService.subnavState === 'full';
  }

  public get isSubNavHidden(): boolean {
    return this.layoutService.subnavState === 'hidden';
  }

  // Expose layoutService for template binding
  public get layout(): LayoutService {
    return this.layoutService;
  }

  // Add this getter for template binding
  public get isMobileScreen(): boolean {
    return this.layoutService.isMobileScreen;
  }

  constructor(
    private readonly authService: AuthService,
    private readonly tokenStorage: TokenStorageService,
    private readonly layoutService: LayoutService,
    private readonly eventsService: EventsService,
    private readonly bsModalService: BsModalService,
    private readonly routeHelper: RouteHelperService,
    private readonly dragulaService: DragulaService,
    private readonly maintenanceModeService: MaintenanceModeService,
    private readonly gainsightService: GainsightService,
    private readonly eidAuthService: EidAuthService,
    private readonly eidSessionService: EidSessionService,
    private readonly cookieService: CookieService,
    private readonly renderer: Renderer2,
    private readonly languageService: LanguageService,
    private readonly appSettingsService: AppSettingsService,
    private readonly http: HttpClientService,
    private readonly customToasters: CustomToastersService,
    private readonly scriptService: ScriptService,
    private readonly router: Router,
    private readonly profileService: ProfileService,
    private readonly powerRecallService: PowerRecallService,
    private readonly powerEngageService: PowerEngageService,
    private readonly configService: ConfigService,
    private readonly froalaCustomCommands: FroalaCustomCommandsService,
    private readonly urlSecurityService: UrlSecurityService
  ) {
    authBroadcastChannel.onmessage = (event) => {
      if (event.data === LOGOUT_REQUESTED_MESSAGE) {
        this.authService.clientLogout(false);
      }
    };
  }

  public ngOnInit(): void {
    this.froalaCustomCommands.registerCustomCommands();

    const userLang = this.languageService.getBrowserLanguage();
    this.renderer.setAttribute(document.documentElement, 'lang', userLang);

    if (this.checkIfOldEidSession()) {
      this.tokenStorage.clearStorageData();
    } else if (this.isEidSessionCheckRequired()) {
      this.validateEidSession();
    }

    const routerSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const currentRoute = this.router.routerState.root.snapshot.firstChild;
        this.isStandaloneRoute = currentRoute?.data?.standalone || false;
      }
    });
    this.subscriptions.push(routerSub);

    this.subscriptions.push(
      this.tokenStorage.userInfoChanged.subscribe((userInfo) => {
        this.checkInitialPassword();
        this.initGainsight(userInfo);
        this.initUserWay(userInfo);
        this.initThemePreference(userInfo);
        if (!userInfo.CurrentSite.PlatformTopNavEnabled) {
          this.layoutService.adjustLayoutForLegacySidebar();
        } else {
          this.layoutService.adjustLayoutForSubnav();
        }

        this.eidAuthService.isAuthenticated.subscribe((isEidAuthenticated) => {
          if (this.shouldEnableEidFeatures(isEidAuthenticated)) {
            this.setupDMSFeatures();
          }
        });
      })
    );

    this.checkInitialPassword();
    this.initGainsight(this.tokenStorage.userInfo);
    this.initUserWay(this.tokenStorage.userInfo);
    this.initThemePreference(this.tokenStorage.userInfo);

    if (this.tokenStorage.userInfo) {
      this.eidAuthService.isAuthenticated.subscribe((isEidAuthenticated) => {
        if (this.shouldEnableEidFeatures(isEidAuthenticated)) {
          this.setupDMSFeatures();
        }
      });
    }

    this.subscriptions.push(this.eventsService.subscribe(SystemEvent.DropUpOpened, () => (this.isDropupOpened = true)));

    this.subscriptions.push(
      this.eventsService.subscribe(SystemEvent.DropUpClosed, () => (this.isDropupOpened = false))
    );

    this.subscriptions.push(this.dragulaService.dragend().subscribe(this.handleDragEnd.bind(this)));

    const timer = window.setTimeout(() => {
      const eidAuthSub = this.eidAuthService.isAuthenticated.subscribe((isAuthenticated) => {
        if (this.shouldEnableEidFeatures(isAuthenticated)) {
          this.eidSessionService.handleSession();
        }
      });
      this.subscriptions.push(eidAuthSub);
    }, 10000);
    this.timers.push(timer);

    const programSwitcherSub = this.layoutService.showProgramSwitcher$.subscribe((val) => {
      this.showProgramSwitcher = val;
    });
    this.subscriptions.push(programSwitcherSub);

    this.resizeSubscription = fromEvent(window, 'resize').subscribe(() => {
      if (window.innerWidth >= 1024) {
        this.layoutService.showProgramSwitcher = false;
      }
    });

    this.layoutService.showProgramSwitcher = false;

    // Global handler: Make all links in Froala readonly containers open in new tab
    // Uses event delegation for performance - single listener handles all clicks
    this.renderer.listen('document', 'click', (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if clicked element is a link inside a froala-readonly-container
      if (target.tagName === 'A' && target.closest('.froala-readonly-container')) {
        const link = target as HTMLAnchorElement;
        const rawHref = link.getAttribute('href');
        // Skip anchor links (internal page navigation)
        if (rawHref && !rawHref.startsWith('#')) {
          event.preventDefault();
          this.urlSecurityService.safeOpenExternal(rawHref);
        }
      }
    });
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x.unsubscribe());
    if (this.resizeSubscription) {
      this.resizeSubscription.unsubscribe();
    }
    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers = [];
  }

  public onOverlayClick(): void {
    this.layoutService.subnavState = SubnavState.hidden;
  }

  onSubnavClosed(): void {
    this.layoutService.subnavState = SubnavState.collapsed;
  }

  private async validateEidSession(): Promise<void> {
    await this.eidAuthService.waitForSessionLoaded();
    const isEidActive = await firstValueFrom(this.eidAuthService.isAuthenticated).catch(() => false);
    if (!isEidActive) {
      this.tokenStorage.clearStorageData();
      await this.eidAuthService.redirectToEidLogin();
    }
  }

  private isEidSessionCheckRequired(): boolean {
    return (
      this.authService.isAuthenticated() &&
      this.tokenStorage.userInfo?.CurrentSite?.PowerSuitEidAuthEnabled &&
      !this.tokenStorage.userInfo?.IsSysAdmin &&
      !location.href.includes('/eid-appswitch') &&
      !location.href.includes('/eid-callback') &&
      !location.href.includes('/eid-loggedin')
    );
  }

  private checkIfOldEidSession(): boolean {
    return this.cookieService.get('eidSession') != 'true' && this.isEidSessionCheckRequired();
  }

  private shouldEnableEidFeatures(isEidAuthenticated: boolean): boolean {
    return (
      isEidAuthenticated &&
      this.authService.isAuthenticated() &&
      this.tokenStorage.userInfo?.CurrentSite?.PowerSuitEidAuthEnabled &&
      !this.tokenStorage.userInfo?.IsSysAdmin
    );
  }

  private checkInitialPassword(): void {
    if (
      this.tokenStorage.userInfo?.IsPasswordInitial &&
      this.authService.changeInitialPasswordModalRef === null &&
      !this.tokenStorage.userInfo?.CurrentSite?.PowerSuitEidAuthEnabled
    ) {
      this.authService.changeInitialPasswordModalRef = this.bsModalService.show(ChangeInitialPasswordModalComponent, {
        backdrop: 'static',
        keyboard: false
      });
    }

    if (!this.authService.isAuthenticated) {
      this.authService.changeInitialPasswordModalRef = null;
    }
  }

  private handleDragEnd({ name, el }): void {
    const parentNode = el.parentNode as Element;

    if (!parentNode) {
      return;
    }

    const commentNode = Array.from(parentNode.childNodes).find((x) => x.nodeType === 8);

    if (!commentNode) {
      return;
    }

    parentNode.insertBefore(commentNode, null);

    (this.dragulaService.find(name).drake as any).cancel(true);
  }

  private initGainsight(userInfo: UserInfo) {
    if (userInfo) {
      this.gainsightService.initGainSight(userInfo);
    }
  }

  private initUserWay(userInfo: UserInfo): void {
    if (this.authService.isAuthenticated()) {
      this.subscriptions.push(
        this.appSettingsService.getUserWayStatus().subscribe(({ EnableUserWay }) => {
          if (EnableUserWay && userInfo.CurrentSite.UserwayEnabled) {
            this.scriptService
              .loadScript(ScriptService.SCRIPT_NAMES.USERWAY, 'text/javascript', { 'data-account': 'evqM0P2xsi' })
              .then(({ loaded }) => {
                if (!loaded) {
                  this.customToasters.customError('Error loading UserWay. Contact support.');
                }
                const script = this.renderer.createElement('script');
                script.text = `showUserWay()`;
                this.renderer.appendChild(document.body, script);
              });
          } else {
            this.scriptService.removeScript(ScriptService.SCRIPT_NAMES.USERWAY);

            const script = this.renderer.createElement('script');
            script.text = `hideUserWay()`;
            this.renderer.appendChild(document.body, script);
          }
        })
      );
    }
  }

  private initThemePreference(userInfo: UserInfo): void {
    if (!userInfo || !this.authService.isAuthenticated()) {
      return;
    }
    this.profileService.getThemePreference().subscribe({
      next: (isNightMode: boolean) => {
        this.isNightMode = isNightMode;
        if (isNightMode) {
          this.layoutService.enableNightMode();
        } else {
          this.layoutService.enableDayMode();
        }
        this.profileService.themePreferenceChanged$.next(isNightMode);
      }
    });
  }

  private setupDMSFeatures() {
    if (this.dmsFeaturesInitialized) {
      return;
    }

    const powerDmsEnabled = this.tokenStorage.userInfo.CurrentSite.PowerDmsEnabled;
    const securesigninEnabled = this.tokenStorage.userInfo.CurrentSite.SecureSignInEnabled;

    const eidSub = this.eidAuthService.accessToken.subscribe((accessToken) => {
      if (accessToken == null) return;

      this.setPowerEngageStatus(accessToken);

      this.dmsFeaturesInitialized = true;

      if (securesigninEnabled) {
        this.setupDMSFeaturesIfHasProductAccess(accessToken, powerDmsEnabled);
      } else {
        if (powerDmsEnabled) {
          this.setSiteKeyCookie(accessToken);
        }
        this.setRecallStatus(accessToken);
      }
    });
    this.subscriptions.push(eidSub);
  }

  private setupDMSFeaturesIfHasProductAccess(accessToken: string, powerDmsEnabled: boolean) {
    const pdmsAccessSub = this.isPDMSInProductAccess(accessToken).subscribe({
      next: (hasAccess: boolean) => {
        if (hasAccess) {
          if (powerDmsEnabled) {
            this.setSiteKeyCookie(accessToken);
          }
          this.setRecallStatus(accessToken);
        } else {
          if (powerDmsEnabled) {
            this.customToasters.customError(
              'PDMS is not active for this tenant. However PDMS Filepicker Add-on is. Contact support.'
            );
          }
        }
      },
      error: () => {
        this.customToasters.customError('Error accessing the product access api for this tenant. Contact support.');
      }
    });
    this.subscriptions.push(pdmsAccessSub);
  }

  public isPDMSInProductAccess(eidToken: string): Observable<any> {
    const url = this.configService.getConfig().authSettings.authServerUrl + `/accounts/product-access`;

    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: `Bearer ${eidToken}`,
        'Content-Type': 'application/json'
      })
    };

    return this.http.get<any>(url, httpOptions, true).pipe(
      map((response) => {
        return response.some((product) => product.productCode === 'PDMS');
      }),
      catchError((error) => {
        return error;
      })
    );
  }

  public isPowerEngageInProductAccess(eidToken: string): Observable<any> {
    const url = this.configService.getConfig().authSettings.authServerUrl + `/accounts/product-access`;

    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: `Bearer ${eidToken}`,
        'Content-Type': 'application/json'
      })
    };

    return this.http.get<any>(url, httpOptions, true).pipe(
      map((response) => {
        return response.some((product) => product.productCode === 'PENGAGE');
      }),
      catchError((error) => {
        return error;
      })
    );
  }

  private setPowerEngageStatus(accessToken: string): void {
    const powerEngageSub = this.isPowerEngageInProductAccess(accessToken).subscribe(
      (isEnabled) => {
        this.powerEngageService.setEnabled(isEnabled);
      },
      () => {
        this.powerEngageService.setEnabled(false);
      }
    );
    this.subscriptions.push(powerEngageSub);
  }

  public setSiteKeyCookie(accessToken: string) {
    this.getDMSSiteKey(accessToken).subscribe(
      (siteKey) => {
        this.cookieService.set('dmssitekey', siteKey);
      },
      () => {
        this.customToasters.customError('Error retrieving site key for this tenant. Contact support.');
      }
    );
  }

  public getDMSSiteKey(eidToken: string): Observable<any> {
    const url = this.configService.getConfig().powerDMSGraphqlBaseURL + `/graphql`;

    const body = {
      query: 'query { sites{ siteProperties { siteKey }}}'
    };

    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: `Bearer ${eidToken}`,
        'Content-Type': 'application/json'
      })
    };

    return this.http.post<DMSGraphQLResponse>(url, body, httpOptions, true).pipe(
      map((response) => {
        return response.data.sites.siteProperties.siteKey;
      }),
      catchError((error) => {
        return error;
      })
    );
  }

  private setRecallStatus(accessToken: string): void {
    const powerRecallSub = this.isPowerRecallEnabled(accessToken).subscribe(
      (isEnabled) => {
        this.powerRecallService.setEnabled(isEnabled);
      },
      () => {
        this.powerRecallService.setEnabled(false);
        this.customToasters.customError('Error retrieving power recall status for this tenant. Contact support.');
      }
    );
    this.subscriptions.push(powerRecallSub);
  }

  public isPowerRecallEnabled(eidToken: string): Observable<any> {
    const url = this.configService.getConfig().powerDMSGraphqlBaseURL + `/graphql`;

    const body = {
      query: 'query fto_getFeature { sites { enabledFeatures(where: { featureId: { eq: 141 } }) { featureName } } }'
    };

    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: `Bearer ${eidToken}`,
        'Content-Type': 'application/json'
      })
    };

    return this.http.post<any>(url, body, httpOptions, true).pipe(
      map((response) => {
        return response.data.sites.enabledFeatures.length > 0;
      }),
      catchError((error) => {
        return error;
      })
    );
  }

  // handle showing subnav from top-sub-nav
  public onShowSubnav(): void {
    if (this.layoutService.isMobileScreen) {
      this.layoutService.subnavState =
        this.layoutService.subnavState === SubnavState.full ? SubnavState.hidden : SubnavState.full;
    } else if (window.innerWidth < 1024) {
      this.layoutService.subnavState =
        this.layoutService.subnavState === SubnavState.collapsed ? SubnavState.full : SubnavState.collapsed;
    } else {
      this.layoutService.subnavState =
        this.layoutService.subnavState === SubnavState.full ? SubnavState.collapsed : SubnavState.full;
      this.subnavComponent?.['adjustLayout']?.();
    }
  }

  openProgramSwitcher(requested?: boolean) {
    // Not needed anymore, but if called, just set the value
    if (typeof requested === 'boolean') {
      this.layoutService.showProgramSwitcher = requested;
    }
  }

  hideProgramSwitcher() {
    this.layoutService.showProgramSwitcher = false;
  }
}

interface DMSGraphQLResponse {
  data: {
    sites: {
      siteProperties: {
        siteKey: string;
      };
    };
  };
}
